import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TestBadge, EmptyState } from "@/components/common";
import {
  SETUP_CAP, confirmPhraseFor, undoPhraseFor, reasonText, planSummary,
} from "@/components/ExistingCaseWorkflowSetup";
import { ChevronLeft, CheckCircle2, ListChecks, History, ShieldCheck, Undo2 } from "lucide-react";

// Process / Cases -> Existing Case Workflow Setup (2026-09). The review
// screen behind the admin-only Process banner: Cases created before the new
// workflow, each shown with its recorded stage, the stage it will have in the
// new workflow, what confirming creates (first task + Next Action) and
// whether an admin must choose the stage. Exact matches are confirmed in one
// bulk action; everything else needs a stage picked from that product's own
// workflow stages. Confirming uses the existing, unchanged apply endpoint
// (typed phrase, every Case re-checked server-side before anything is saved).
// Until a Case is confirmed it stays visible everywhere — only its stage
// changes stay blocked by the server.

const SAFE = "safe_automatic";
const MANUAL = "ambiguous_manual_review";

const HISTORY_STATUS = {
  applied: { label: "Set up", cls: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30" },
  rolled_back: { label: "Undone", cls: "bg-muted text-muted-foreground" },
};

const Plan = ({ tasks, next, testId }) => {
  const p = planSummary(tasks, next);
  return (
    <div className="space-y-0.5" data-testid={testId}>
      {p.tasks.length === 0 ? (
        <p className="text-muted-foreground">No task at this stage</p>
      ) : p.tasks.map((t) => (
        <p key={t.title}>
          <span className="text-muted-foreground">Task:</span> {t.title}
          {t.optional && <span className="text-muted-foreground"> (optional)</span>}
          {t.waitsForOwner && <span className="text-[hsl(var(--warning))]"> — created once a Process owner is assigned</span>}
        </p>
      ))}
      {p.nextAction && <p><span className="text-muted-foreground">Next Action:</span> <b>{p.nextAction}</b></p>}
    </div>
  );
};

const CustomerCell = ({ r }) => (
  <TableCell className="text-xs">
    <div className="flex items-center gap-1.5">
      <span className="font-medium text-sm">{r.customer_name || "—"}</span>
      <TestBadge show={r.is_test_record} />
    </div>
    <Link to={`/process/${r.case_id}`} className="font-mono text-[11px] text-muted-foreground underline decoration-dotted">
      {r.case_id}
    </Link>
    {r.customer_id && <span className="font-mono text-[11px] text-muted-foreground"> · {r.customer_id}</span>}
  </TableCell>
);

const ProductCell = ({ r }) => (
  <TableCell className="text-xs">
    <p className="text-sm">{r.product_code}</p>
    {r.workflow_label && <p className="text-muted-foreground">{r.workflow_label}</p>}
  </TableCell>
);

const Reasons = ({ r }) => (
  <div className="space-y-0.5 text-[11px] text-muted-foreground max-w-80">
    {(r.reasons || []).map((x, i) => <p key={i}>{reasonText(x, r)}</p>)}
  </div>
);

const SetupTableHead = () => (
  <TableHeader>
    <TableRow>
      <TableHead>Customer</TableHead>
      <TableHead>Product</TableHead>
      <TableHead>Recorded stage</TableHead>
      <TableHead>Stage in new workflow</TableHead>
      <TableHead>First task → Next Action</TableHead>
      <TableHead>Confirmation</TableHead>
    </TableRow>
  </TableHeader>
);

// ---------------------------------------------------------------- confirm
const ConfirmSetupDialog = ({ items, rowsById, onClose, onDone }) => {
  const [phrase, setPhrase] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const open = !!items && items.length > 0;
  useEffect(() => { setPhrase(""); setNote(""); }, [items]);
  if (!open) return null;
  const n = items.length;
  const expected = confirmPhraseFor(n);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/case-adoption/apply", {
        items, confirmation_phrase: phrase.trim(), note: note.trim() || null,
      });
      onDone(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.errors) {
        toast.error(`${d.message} ${d.errors.map((x) => `${x.case_id}: ${x.error}`).join(" | ")}`, { duration: 12000 });
      } else toast.error(errDetail(e));
    } finally { setBusy(false); }
  };
  const shown = items.slice(0, 8);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="wfs-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Confirm workflow setup for {n} Case{n === 1 ? "" : "s"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            Each Case starts the new workflow at the stage shown, its first task is created and its Next Action
            appears. History, documents, payments, notes and owners stay exactly as they are. Every Case is
            re-checked just before it&apos;s saved, and a setup can be undone from Setup history until the Case
            moves on.
          </p>
          <div className="rounded-md border p-2 text-xs space-y-1 max-h-48 overflow-y-auto" data-testid="wfs-confirm-list">
            {shown.map((it) => {
              const r = rowsById[it.case_id] || {};
              const target = it.stage || r.proposed_stage;
              return (
                <p key={it.case_id}>
                  <b>{r.customer_name || it.case_id}</b> <span className="text-muted-foreground">({r.product_code})</span>:{" "}
                  {r.current_stage === target ? <>stays at <b>{target}</b></> : <>{r.current_stage} → <b>{target}</b></>}
                  {it.stage && <span className="text-muted-foreground"> (your choice)</span>}
                </p>
              );
            })}
            {n > shown.length && <p className="text-muted-foreground">…and {n - shown.length} more</p>}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Note (optional — saved with the audit record)</Label>
            <Input data-testid="wfs-confirm-note" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Reviewed with the Process lead" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">To confirm, type <span className="font-mono bg-muted px-1.5 py-0.5 rounded">{expected}</span></Label>
            <Input data-testid="wfs-confirm-phrase" value={phrase} onChange={(e) => setPhrase(e.target.value)}
              placeholder={expected} autoComplete="off" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={onClose}>Cancel</Button>
          <Button data-testid="wfs-confirm-submit" disabled={busy || phrase.trim() !== expected} onClick={submit}>
            {busy ? "Confirming…" : "Confirm setup"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------- history
const UndoDialog = ({ target, onClose, onDone }) => {
  const [phrase, setPhrase] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPhrase(""); setReason(""); }, [target]);
  if (!target) return null;
  const expected = undoPhraseFor(target.case_id);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/case-adoption/rollback", {
        case_id: target.case_id, reason: reason.trim(), confirmation_phrase: phrase.trim(),
      });
      toast.success(`Setup undone for ${data.case_id} — back at '${data.restored_stage}'. It needs setup again before its stage can change.`);
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.reasons ? `${d.message} ${d.reasons.join(" ")}` : errDetail(e), { duration: 10000 });
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent data-testid="wfs-undo-dialog">
        <DialogHeader><DialogTitle className="font-heading">Undo workflow setup — {target.customer_name || target.case_id}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            This puts the Case back exactly as it was before setup (stage &apos;{target.previous_stage}&apos;) and
            removes only the task setup created. It isn&apos;t possible once the Case has moved on under the new
            workflow.
          </p>
          <div className="space-y-1.5">
            <Label className="text-xs">Reason (required)</Label>
            <Input data-testid="wfs-undo-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">To confirm, type <span className="font-mono bg-muted px-1.5 py-0.5 rounded">{expected}</span></Label>
            <Input data-testid="wfs-undo-phrase" value={phrase} onChange={(e) => setPhrase(e.target.value)}
              placeholder={expected} autoComplete="off" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={onClose}>Cancel</Button>
          <Button variant="destructive" data-testid="wfs-undo-submit"
            disabled={busy || phrase.trim() !== expected || !reason.trim()} onClick={submit}>
            {busy ? "Undoing…" : "Undo setup"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SetupHistory = ({ refreshTick, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(null);
  const [undoTarget, setUndoTarget] = useState(null);
  const [check, setCheck] = useState(null);
  useEffect(() => {
    if (!open) return;
    api.get("/admin/case-adoption/adoptions").then((r) => setList(r.data)).catch((e) => {
      setList([]); toast.error(errDetail(e));
    });
  }, [open, refreshTick]);
  const verify = async (s) => {
    try {
      const { data } = await api.get(`/admin/case-adoption/verify?${new URLSearchParams({ case_id: s.case_id })}`);
      setCheck({ case_id: s.case_id, ...data });
      if (data.all_ok) toast.success(`Check passed for ${s.case_id} — everything matches what setup recorded.`);
      else toast.error(`Check found differences for ${s.case_id} — see details below.`);
    } catch (e) { toast.error(errDetail(e)); }
  };
  return (
    <Card data-testid="wfs-history">
      <CardHeader className="pb-2 flex flex-row items-center gap-2 space-y-0">
        <CardTitle className="text-sm font-heading flex items-center gap-2"><History className="h-4 w-4" /> Setup history</CardTitle>
        <Button size="sm" variant="ghost" className="ml-auto h-7 text-xs" data-testid="wfs-history-toggle"
          onClick={() => setOpen((o) => !o)}>{open ? "Hide" : "Show"}</Button>
      </CardHeader>
      {open && (
        <CardContent className="space-y-3">
          {!list ? <Skeleton className="h-16" /> : list.length === 0 ? (
            <p className="text-xs text-muted-foreground">No Cases have been set up yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="wfs-history-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead><TableHead>Stage</TableHead><TableHead>Status</TableHead>
                    <TableHead>When / by</TableHead><TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {list.map((s) => {
                    const st = HISTORY_STATUS[s.status] || { label: String(s.status).replaceAll("_", " "), cls: "" };
                    return (
                      <TableRow key={s.snapshot_id} data-testid="wfs-history-row" data-case-id={s.case_id}>
                        <TableCell className="text-xs">
                          <span className="font-medium">{s.customer_name || "—"}</span> <TestBadge show={s.is_test_record} />
                          <br /><Link className="font-mono text-[11px] text-muted-foreground underline decoration-dotted" to={`/process/${s.case_id}`}>{s.case_id}</Link>
                        </TableCell>
                        <TableCell className="text-xs">
                          {s.previous_stage === s.target_stage ? s.target_stage : <>{s.previous_stage} → {s.target_stage}</>}
                          {s.chosen_by_admin && <span className="text-muted-foreground"> (chosen)</span>}
                          {s.live_stage && s.live_stage !== s.target_stage && <><br /><span className="text-muted-foreground">now: {s.live_stage}</span></>}
                        </TableCell>
                        <TableCell><Badge variant="outline" className={`text-[10px] ${st.cls}`}>{st.label}</Badge></TableCell>
                        <TableCell className="text-xs text-muted-foreground">{fmtIST(s.applied_at || s.taken_at)}<br />{s.actor_name}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`wfs-check-${s.case_id}`}
                            onClick={() => verify(s)}><ShieldCheck className="h-3.5 w-3.5 mr-1" />Check</Button>
                          {s.status === "applied" && (
                            <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`wfs-undo-${s.case_id}`}
                              onClick={() => setUndoTarget(s)}><Undo2 className="h-3.5 w-3.5 mr-1" />Undo</Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          {check && !check.all_ok && (
            <div className="rounded-md border p-2 text-[11px] space-y-0.5" data-testid="wfs-check-result">
              <p className="font-medium">Differences for {check.case_id}:</p>
              {(check.records || []).filter((r) => !r.ok && !r.superseded_by).map((r) => (
                <p key={r.snapshot_id} className="font-mono">{(r.mismatches || []).join(", ")}</p>
              ))}
            </div>
          )}
        </CardContent>
      )}
      <UndoDialog target={undoTarget} onClose={() => setUndoTarget(null)}
        onDone={() => { setUndoTarget(null); onChanged(); }} />
    </Card>
  );
};

// ------------------------------------------------------------------- page
export default function CaseWorkflowSetupPage() {
  const { hasCap } = useAuth();
  const isAdmin = hasCap(SETUP_CAP);
  const [params] = useSearchParams();
  const focusCase = params.get("case") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [choices, setChoices] = useState({});
  const [confirmItems, setConfirmItems] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const scrolled = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/case-adoption/setup-review");
      setData(d);
      // Drop choices for Cases that no longer need one.
      setChoices((c) => Object.fromEntries(Object.entries(c).filter(([cid]) =>
        d.rows.some((r) => r.case_id === cid && r.classification === MANUAL))));
    } catch (e) { toast.error(errDetail(e, "Could not load the Cases needing setup")); } finally { setLoading(false); }
  }, []);
  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const rows = useMemo(() => data?.rows || [], [data]);
  const rowsById = useMemo(() => Object.fromEntries(rows.map((r) => [r.case_id, r])), [rows]);
  const ready = rows.filter((r) => r.classification === SAFE);
  const manual = rows.filter((r) => r.classification === MANUAL);
  const chosen = manual.filter((r) => choices[r.case_id]);
  const maxPer = data?.max_per_confirm || 200;

  useEffect(() => {
    if (!focusCase || scrolled.current || !data) return;
    const el = document.querySelector(`[data-wfs-case="${CSS.escape(focusCase)}"]`);
    if (el) { el.scrollIntoView({ block: "center" }); scrolled.current = true; }
  }, [focusCase, data]);

  const onApplied = (res) => {
    const results = res.results || [];
    const done = results.filter((x) => x.status === "adopted");
    const already = results.filter((x) => ["already_adopted", "skipped"].includes(x.status));
    const failed = results.filter((x) => !["adopted", "already_adopted", "skipped"].includes(x.status));
    if (done.length) toast.success(`${done.length} Case${done.length === 1 ? "" : "s"} set up — first tasks and Next Actions are now live.`);
    if (already.length) toast.info(`${already.length} Case${already.length === 1 ? " was" : "s were"} already set up — nothing changed for ${already.length === 1 ? "it" : "them"}.`);
    if (failed.length) {
      toast.warning(`${failed.length} Case${failed.length === 1 ? "" : "s"} couldn't be set up: `
        + failed.map((x) => `${x.case_id} — ${x.status === "skipped_changed" ? "it changed since you reviewed it" : x.status === "in_progress" ? "already being set up" : (x.error || x.status)}`).join("; ")
        + ". The list has been refreshed.", { duration: 12000 });
    }
    setConfirmItems(null);
    setChoices((c) => Object.fromEntries(Object.entries(c).filter(([cid]) => !done.some((x) => x.case_id === cid))));
    setRefreshTick((t) => t + 1);
    load();
  };

  const rowCls = (r) => (r.case_id === focusCase ? "bg-[hsl(var(--warning))]/10 ring-1 ring-inset ring-[hsl(var(--warning))]/50" : "");

  if (!isAdmin) {
    return (
      <div className="space-y-4" data-testid="wfs-page">
        <Link to="/process" className="text-xs text-muted-foreground flex items-center gap-1 hover:text-foreground"><ChevronLeft className="h-3.5 w-3.5" />Process / Cases</Link>
        <Card className="p-4 text-sm text-muted-foreground" data-testid="wfs-not-admin">
          Only an administrator can complete workflow setup for existing Cases.
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="wfs-page">
      <div>
        <Link to="/process" data-testid="wfs-back" className="text-xs text-muted-foreground flex items-center gap-1 hover:text-foreground w-fit">
          <ChevronLeft className="h-3.5 w-3.5" />Process / Cases
        </Link>
        <h1 className="font-heading text-2xl font-semibold tracking-tight mt-1">Existing Case Workflow Setup</h1>
        <p className="text-sm text-muted-foreground max-w-3xl" data-testid="wfs-intro">
          These Cases were created before the new workflow. Confirm their current stage so mandatory tasks,
          outcomes and Next Actions can begin. Until then they stay visible everywhere as usual, but their stage
          can&apos;t be changed. Confirming never touches a Case&apos;s history, documents, payments, notes or owners.
        </p>
      </div>

      {!data ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12" />)}</div>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState icon={CheckCircle2} title="All existing Cases are set up"
            hint="Every open Case now follows the new workflow — nothing to confirm." testId="wfs-all-done" />
        </Card>
      ) : (
        <>
          {/* ------------------------------ exact matches (bulk) ------------------------------ */}
          <Card data-testid="wfs-ready-section">
            <CardHeader className="pb-2 flex flex-row items-start gap-3 space-y-0 flex-wrap">
              <div className="min-w-0">
                <CardTitle className="text-sm font-heading flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" /> Ready to confirm
                  <Badge variant="outline" data-testid="wfs-ready-count">{ready.length}</Badge>
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  The recorded stage matches a stage in the new workflow and nothing conflicts with it. Review the
                  list and confirm them all at once.
                </p>
              </div>
              {ready.length > 0 && (
                <Button size="sm" className="ml-auto" data-testid="wfs-confirm-all-ready" disabled={loading}
                  onClick={() => setConfirmItems(ready.slice(0, maxPer).map((r) => ({ case_id: r.case_id })))}>
                  {ready.length > maxPer ? `Confirm the first ${maxPer}` : `Confirm all ${ready.length}`}
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {ready.length === 0 ? (
                <p className="text-xs text-muted-foreground" data-testid="wfs-ready-empty">No Case can be confirmed automatically — each one below needs you to choose its stage.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="wfs-ready-table">
                    <SetupTableHead />
                    <TableBody>
                      {ready.map((r) => (
                        <TableRow key={r.case_id} data-testid="wfs-ready-row" data-wfs-case={r.case_id} className={rowCls(r)}>
                          <CustomerCell r={r} />
                          <ProductCell r={r} />
                          <TableCell className="text-sm">{r.current_stage}</TableCell>
                          <TableCell className="text-sm font-medium" data-testid="wfs-proposed-stage">{r.proposed_stage}</TableCell>
                          <TableCell className="text-xs"><Plan tasks={r.entry_tasks} next={r.proposed_next_action} testId="wfs-plan" /></TableCell>
                          <TableCell className="text-xs space-y-1">
                            <Badge variant="outline" data-testid="wfs-confirmation-kind"
                              className="text-[10px] bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30">
                              No choice needed
                            </Badge>
                            <Reasons r={r} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* -------------------------- needs a stage choice (manual) -------------------------- */}
          <Card data-testid="wfs-manual-section">
            <CardHeader className="pb-2 flex flex-row items-start gap-3 space-y-0 flex-wrap">
              <div className="min-w-0">
                <CardTitle className="text-sm font-heading flex items-center gap-2">
                  <ListChecks className="h-4 w-4 text-[hsl(var(--warning))]" /> Choose the current stage
                  <Badge variant="outline" data-testid="wfs-manual-count">{manual.length}</Badge>
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  These can&apos;t be matched automatically. Pick where each Case really is from its product&apos;s
                  workflow stages — a stage is never guessed for you.
                </p>
              </div>
              {manual.length > 0 && (
                <Button size="sm" className="ml-auto" data-testid="wfs-confirm-chosen" disabled={loading || chosen.length === 0}
                  onClick={() => setConfirmItems(chosen.slice(0, maxPer).map((r) => ({ case_id: r.case_id, stage: choices[r.case_id] })))}>
                  Confirm {chosen.length} chosen stage{chosen.length === 1 ? "" : "s"}
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {manual.length === 0 ? (
                <p className="text-xs text-muted-foreground" data-testid="wfs-manual-empty">Nothing needs a stage choice.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="wfs-manual-table">
                    <SetupTableHead />
                    <TableBody>
                      {manual.map((r) => {
                        const pick = choices[r.case_id];
                        const stageInfo = (r.valid_stages || []).find((s) => s.name === pick);
                        const canPick = (r.valid_stages || []).some((s) => s.selectable);
                        return (
                          <TableRow key={r.case_id} data-testid="wfs-manual-row" data-wfs-case={r.case_id} className={rowCls(r)}>
                            <CustomerCell r={r} />
                            <ProductCell r={r} />
                            <TableCell className="text-sm">{r.current_stage}</TableCell>
                            <TableCell className="text-xs">
                              {canPick ? (
                                <>
                                  <Select value={pick || ""} onValueChange={(v) => setChoices((c) => ({ ...c, [r.case_id]: v }))}>
                                    <SelectTrigger data-testid={`wfs-stage-picker-${r.case_id}`} className="h-8 w-56 text-xs">
                                      <SelectValue placeholder="Choose stage…" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {r.valid_stages.map((s) => (
                                        <SelectItem key={s.name} value={s.name} disabled={!s.selectable}
                                          title={(s.why_not || []).join(" ")} data-testid={`wfs-stage-option-${s.name}`}>
                                          {s.name}{!s.selectable ? " — not available" : ""}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  {/* Only offered when it can actually be chosen — a
                                      gated suggestion is explained by the reason instead. */}
                                  {r.suggested_stage && !pick
                                    && (r.valid_stages || []).some((s) => s.name === r.suggested_stage && s.selectable) && (
                                    <p className="text-[11px] text-muted-foreground mt-1">Likely match: {r.suggested_stage} (please confirm)</p>
                                  )}
                                  {pick && (
                                    <button type="button" className="text-[11px] text-muted-foreground underline mt-1"
                                      onClick={() => setChoices((c) => { const n = { ...c }; delete n[r.case_id]; return n; })}>clear</button>
                                  )}
                                </>
                              ) : <span className="text-muted-foreground">Can&apos;t be set up yet — see why</span>}
                            </TableCell>
                            <TableCell className="text-xs">
                              {stageInfo ? (
                                <Plan tasks={stageInfo.entry_tasks} next={stageInfo.next_action} testId="wfs-plan" />
                              ) : <span className="text-muted-foreground">Choose a stage to see this</span>}
                            </TableCell>
                            <TableCell className="text-xs space-y-1">
                              <Badge variant="outline" data-testid="wfs-confirmation-kind"
                                className="text-[10px] bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30">
                                Your choice required
                              </Badge>
                              <Reasons r={r} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {data?.other_blocked > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="wfs-other-blocked">
          {data.other_blocked} other open Case{data.other_blocked === 1 ? " is" : "s are"} also waiting on the new
          workflow but can&apos;t be set up here — {data.other_blocked === 1 ? "its" : "their"} product&apos;s workflow
          must be updated first.
        </p>
      )}

      <SetupHistory refreshTick={refreshTick} onChanged={() => { setRefreshTick((t) => t + 1); load(); }} />

      <ConfirmSetupDialog items={confirmItems} rowsById={rowsById}
        onClose={() => setConfirmItems(null)} onDone={onApplied} />
    </div>
  );
}
